import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Chapter, MaskLines } from "../components/Reveal";
import { INDUSTRIES } from "../data/content";

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <SEO
        title="Industries Served"
        description="Taxation, accounting and compliance support for traders, manufacturers, professionals, startups, real estate, education, healthcare, e-commerce, hospitality, logistics, salaried individuals and NRIs."
        path="/industries"
      />

      <section className="relative bg-navy text-white overflow-hidden" data-testid="industries-hero">
        <div className="grain-overlay" />
        <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-24">
          <p className="eyebrow mb-6">Who We Serve</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="industries-heading">
            <MaskLines lines={["Every Sector."]} />
            <MaskLines lines={["One Standard of Care."]} lineClassName="text-gold italic" baseDelay={0.3} />
          </h1>
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-slate-300 leading-relaxed">
              The firm serves clients across a wide range of sectors and organisational forms. Each engagement is
              approached on its own facts, with services tailored to the compliance obligations of that sector.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background" data-testid="industries-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="01" label="Sectors" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.07}>
                <div
                  className="group h-full rounded-xl border border-slate-200/80 bg-white p-7 hover:border-gold/60 hover:shadow-xl hover:shadow-navy/10 transition-all duration-300"
                  data-testid={`industry-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center group-hover:bg-navy transition-colors">
                      <ind.icon size={22} className="text-navy group-hover:text-gold transition-colors" strokeWidth={1.7} />
                    </span>
                    <span className="font-mono text-xs text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-navy">{ind.name}</h3>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div className="rounded-2xl bg-navy text-white p-10 sm:p-14 relative overflow-hidden" data-testid="industries-cta">
              <div className="grain-overlay" />
              <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
                <div>
                  <p className="eyebrow">Not Listed Here?</p>
                  <h2 className="mt-4 font-serif text-2xl sm:text-3xl font-semibold max-w-xl">
                    Speak with us about your specific sector and requirements.
                  </h2>
                </div>
                <Link
                  to="/contact"
                  data-testid="industries-cta-button"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy hover:bg-gold-bright transition-colors shrink-0"
                >
                  Book a Consultation <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
